import type { FastTrackShareClassesResponse } from "../../../api/src/fasttrack/refs.service";
import type {
  MarketDataPart,
  MarketDataShareClassHeld,
} from "../../../api/src/security-details/security-details.types";
import { formatCurrency } from "../lib/numbers";
import { isEmptyValue, NOT_AVAILABLE } from "./fastTrackValues";

// APP-1487 (doc-security-market-data-01a150c533210e70): the share-class flags
// of a mutual fund (R98 to R108). Both expense ratios of each comparison come
// from the same `/ref/<symbol>/shareclasses` answer (D15).

/** The last sentence of the "Share classes" flag (R107). */
export const ELIGIBILITY_SENTENCE =
  "Check the eligibility of each class before you treat it as an option.";

/** A class that the "Share classes" flag names, with its four values (R106). */
export type LowerShareClass = {
  shareClass: string;
  ticker: string;
  expenseRatio: string;
  minimumInitial: string;
};

export type ShareClassFlag =
  | { kind: "share-classes"; classes: LowerShareClass[] }
  | { kind: "pricier-held"; ticker: string; text: string };

/**
 * An expense ratio of the answer, a fraction string such as "0.003100", in
 * millionths, so that a difference of two ratios has no float error. Null for
 * an empty value (R102, R104).
 */
const millionthsOf = (value: unknown): number | null => {
  if (isEmptyValue(value)) return null;
  const number = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(number) ? Math.round(number * 1_000_000) : null;
};

/** Millionths as a percent with 2 decimals: 3100 is "0.31%". */
const ratioText = (millionths: number) =>
  `${(millionths / 10_000).toFixed(2)}%`;

const textOf = (value: unknown) =>
  isEmptyValue(value) ? NOT_AVAILABLE : String(value).trim();

/** A minimum initial purchase in whole US dollars, or `NOT_AVAILABLE` (R40). */
const minimumText = (value: unknown) => {
  if (isEmptyValue(value)) return NOT_AVAILABLE;
  const number = parseFloat(String(value));
  return Number.isFinite(number) ? formatCurrency(number) : NOT_AVAILABLE;
};

const tickerKey = (ticker: unknown) =>
  typeof ticker === "string" ? ticker.trim().toUpperCase() : "";

const count = (value: number, noun: string) =>
  `${value} ${noun}${value === 1 ? "" : "s"}`;

/**
 * The share-class flags of the page of `symbol`:
 *
 * - the "Share classes" flag, when another class has a lower expense ratio
 *   than the class of the page, naming each lower class, lowest first (R98,
 *   R105, R106);
 * - one "Pricier share class held" flag for each class the firm holds with a
 *   higher expense ratio, in the order of the answer (R99, R108). The yearly
 *   amount is the market value times the difference of the two ratios,
 *   rounded to the nearest $10 (D16).
 *
 * No flag when the security type is not `Mutual Fund` (R100), the call failed
 * (R101), no class has the symbol of the page (R103), or the class of the
 * page has an empty expense ratio (R104). A class with an empty expense ratio
 * is never named (R102).
 */
export function shareClassFlags({
  symbol,
  securityType,
  shareClasses,
  shareClassesHeld,
}: {
  symbol: string;
  securityType: string;
  shareClasses: MarketDataPart<FastTrackShareClassesResponse> | null;
  shareClassesHeld: MarketDataShareClassHeld[] | null;
}): ShareClassFlag[] {
  if (securityType !== "Mutual Fund") return [];
  if (shareClasses?.status !== "ok") return [];
  const page = tickerKey(symbol);
  const classes = shareClasses.data?.datalist ?? [];
  const pageClass = classes.find((c) => tickerKey(c.ticker) === page);
  if (!pageClass) return [];
  const pageRatio = millionthsOf(pageClass.expense_ratio);
  if (pageRatio === null) return [];

  // Each other class once, with a ratio; the first entry of a ticker wins.
  const seen = new Set([page]);
  const others: { shareClass: (typeof classes)[number]; ratio: number }[] = [];
  for (const shareClass of classes) {
    const key = tickerKey(shareClass.ticker);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const ratio = millionthsOf(shareClass.expense_ratio);
    if (ratio !== null) others.push({ shareClass, ratio });
  }

  const flags: ShareClassFlag[] = [];
  const lower = others
    .filter(({ ratio }) => ratio < pageRatio)
    .sort((a, b) => a.ratio - b.ratio);
  if (lower.length > 0) {
    flags.push({
      kind: "share-classes",
      classes: lower.map(({ shareClass, ratio }) => ({
        shareClass: textOf(shareClass.shareclass),
        ticker: shareClass.ticker.trim(),
        expenseRatio: ratioText(ratio),
        minimumInitial: minimumText(shareClass.initial_purchase),
      })),
    });
  }

  const held = new Map(
    (shareClassesHeld ?? []).map((entry) => [
      tickerKey(entry.ticker),
      entry.held,
    ]),
  );
  for (const { shareClass, ratio } of others) {
    if (ratio <= pageRatio) continue;
    const position = held.get(tickerKey(shareClass.ticker));
    if (!position) continue;
    const ticker = shareClass.ticker.trim();
    const yearly =
      Math.round((position.marketValue * (ratio - pageRatio)) / 10_000_000) *
      10;
    flags.push({
      kind: "pricier-held",
      ticker,
      text:
        `Pricier share class held. ${count(position.accounts, "account")} ` +
        `in ${count(position.households, "household")} hold ${ticker}, the ` +
        `same portfolio at ${ratioText(ratio)}: ` +
        `${formatCurrency(Math.round(position.marketValue))}, about ` +
        `${formatCurrency(yearly)} a year more than ${pageClass.ticker.trim()}.`,
    });
  }
  return flags;
}

/**
 * The "Flags" list of the fund profile, below the "Share classes" list (D14):
 * each share-class flag of `shareClassFlags`, starting with "⚑". Nothing shows
 * when there is no flag.
 */
export const ShareClassFlags = ({ flags }: { flags: ShareClassFlag[] }) => {
  if (flags.length === 0) return null;
  return (
    <section aria-label="Flags" className="mt-3">
      <h4 className="fs-6 fw-semibold">Flags</h4>
      <ul className="list-unstyled mb-0">
        {flags.map((flag) =>
          flag.kind === "share-classes" ? (
            <li key="share-classes" data-testid="flag-share-classes">
              ⚑ Share classes. FastTrack lists{" "}
              {flag.classes.length === 1
                ? "a class"
                : `${flag.classes.length} classes`}{" "}
              with a lower expense ratio:
              <ul>
                {flag.classes.map((c) => (
                  <li key={c.ticker}>
                    {c.shareClass} ({c.ticker}) at {c.expenseRatio}, minimum
                    initial purchase {c.minimumInitial}
                  </li>
                ))}
              </ul>
              {ELIGIBILITY_SENTENCE}
            </li>
          ) : (
            <li key={`pricier-${flag.ticker}`} data-testid="flag-pricier-held">
              ⚑ {flag.text}
            </li>
          ),
        )}
      </ul>
    </section>
  );
};
